/**
 * Amounts Service
 * Handles amount-related API calls (for center phase fees).
 */

import api from '../utils/api';

/**
 * Get all amounts
 * GET /v1/amounts/
 */
export const getAmounts = async () => {
  try {
    const response = await api.get('/v1/amounts/');
    const list = Array.isArray(response) ? response : response?.data ?? [];
    if (Array.isArray(list)) {
      return { success: true, data: list, message: 'Amounts retrieved successfully' };
    }
    if (response?.status === 'success' && response?.data) {
      return { success: true, data: response.data, message: response.message || 'Amounts retrieved successfully' };
    }
    return { success: false, error: response?.message || 'Failed to retrieve amounts', data: null };
  } catch (error) {
    console.error('Get amounts error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to retrieve amounts. Please try again.';
    return { success: false, error: errorMessage, data: null };
  }
};

/**
 * Create an amount
 * POST /v1/amounts/
 * @param {{ value: number, name?: string }} payload
 */
export const createAmount = async (payload) => {
  try {
    const response = await api.post('/v1/amounts/', {
      value: Number(payload.value),
      name: payload.name?.trim() || undefined,
    });
    const data = response?.data ?? response;
    if (response?.status === 'success' && data) {
      return { success: true, data, message: response.message || 'Amount created successfully' };
    }
    if (data?.id) {
      return { success: true, data, message: 'Amount created successfully' };
    }
    return { success: false, error: response?.message || 'Failed to create amount', data: null };
  } catch (error) {
    console.error('Create amount error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to create amount. Please try again.';
    return { success: false, error: errorMessage, data: null };
  }
};

/**
 * Update an amount
 * PATCH /v1/amounts/{amount_id}
 * Body: { value?: number, name?: string }
 * When value is updated, student tuitions using this amount are updated.
 */
export const updateAmount = async (amountId, payload) => {
  try {
    const body = {};
    if (payload.value !== undefined) body.value = Number(payload.value);
    if (payload.name !== undefined) body.name = payload.name?.trim() ?? undefined;
    const response = await api.patch(`/v1/amounts/${amountId}`, body);
    const data = response?.data ?? response;
    if (response?.status === 'success' && data) {
      return { success: true, data, message: response.message || 'Amount updated successfully' };
    }
    if (data?.id) {
      return { success: true, data, message: 'Amount updated successfully' };
    }
    return { success: false, error: response?.message || 'Failed to update amount', data: null };
  } catch (error) {
    console.error('Update amount error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to update amount. Please try again.';
    return { success: false, error: errorMessage, data: null };
  }
};

/**
 * Delete an amount
 * DELETE /v1/amounts/{amount_id}
 */
export const deleteAmount = async (amountId) => {
  try {
    const response = await api.delete(`/v1/amounts/${amountId}`);
    if (response?.status === 'success' || response?.message) {
      return { success: true, message: response.message || 'Amount deleted successfully' };
    }
    return { success: false, error: response?.message || response?.error || 'Failed to delete amount' };
  } catch (error) {
    console.error('Delete amount error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to delete amount. It may be in use by a center.';
    return { success: false, error: errorMessage };
  }
};

const amountsService = { getAmounts, createAmount, updateAmount, deleteAmount };
export default amountsService;
